import React, {useState, useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, Typography, Divider, Stack} from '@mui/material';
import {Body, SwitchButton} from '@lightricks/react-design-system';
import onboardingWizardStore, {
  onboardingWizardActions,
} from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizardStore';
import {getEnv} from '@/config/environment';
import type {ModestConfig, TransformedExperimentGroup} from '@/types/modest';
import Modal from '@/components/modal/Modal';
import {useModestWebContext} from '@/contexts/ModestWebContext';
import modestExperimentOverrideStore, {
  modestExperimentOverrideActions,
  type ExperimentOverride,
} from '@/stores/modestExperimentOverrideStore';
import AddOverrideSection from './AddOverrideSection';
import CurrentOverridesSection from './CurrentOverridesSection';
import styles from './DeveloperOverrideModal.module.scss';

interface DeveloperOverrideModalProps {
  testID?: string;
}

function DeveloperOverrideModal({
  testID = 'modest-experiment-override-modal',
}: DeveloperOverrideModalProps) {
  const {debugLogger, config} = useModestWebContext();
  const {isModalOpen, availableExperiments, isEnabled} =
    modestExperimentOverrideStore();

  const currentOverrides = modestExperimentOverrideActions.getCurrentOverrides(
    config.appId
  );

  const [selectedExperiment, setSelectedExperiment] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string>('');

  const forceOnboarding = onboardingWizardStore(
    useShallow((state) => state.forceOnboarding)
  );

  // Load experiments when modal opens
  useEffect(() => {
    if (isModalOpen && isEnabled && config.appId) {
      const loadExperiments = async () => {
        try {
          // Determine environment and build API URL
          const env = getEnv().MODE === 'production' ? 'prod' : 'dev';
          const apiUrl = `https://modest.lightricks.com/apps/${config.appId}/${env}.json`;

          debugLogger('🔍 Modal: Fetching experiments from:', apiUrl);

          // Fetch experiments from Modest API
          const response = await fetch(apiUrl);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch experiments: ${response.status} ${response.statusText}`
            );
          }

          const modestConfig = (await response.json()) as ModestConfig;
          debugLogger('🔍 Modal: Raw Modest config:', modestConfig);

          // Extract experiment groups from the config
          const experimentGroups = modestConfig.experimentGroups || [];
          debugLogger(
            '🔍 Modal: Raw experiment groups from API:',
            experimentGroups
          );

          // Transform Modest experiment groups to our format
          const transformedExperimentGroups: TransformedExperimentGroup[] =
            experimentGroups
              .map((group) => ({
                groupName: group.name,
                variants:
                  group.variants?.map((variant) => ({
                    name: variant.canonicalName,
                    id: variant.id,
                    displayName: variant.displayName || variant.canonicalName,
                  })) || [],
              }))
              .filter((group) => group.variants.length > 0);

          debugLogger(
            '🔍 Modal: Transformed experiment groups:',
            transformedExperimentGroups
          );

          modestExperimentOverrideActions.setAvailableExperimentGroups(
            transformedExperimentGroups
          );
          debugLogger(
            'Modal: Loaded live experiment groups from API',
            transformedExperimentGroups
          );
        } catch (error) {
          debugLogger('Modal: Failed to load experiments from API:', error);
        }
      };

      loadExperiments();
    }
  }, [isModalOpen, isEnabled, config.appId, debugLogger]);

  // Convert available experiment groups to the format we need for the Select component
  // Filter out experiment groups that already have overrides, but always include the currently selected one
  const experimentOptions = availableExperiments
    .filter(
      (group) =>
        group.groupName === selectedExperiment ||
        !currentOverrides.some(
          (override) => override.groupName === group.groupName
        )
    )
    .map((group) => ({
      id: group.groupName,
      label: group.groupName,
    }));

  const selectedExperimentVariants =
    availableExperiments
      .find((group) => group.groupName === selectedExperiment)
      ?.variants.map((variant) => ({
        id: variant.name,
        label: variant.displayName,
      })) || [];

  // Debug logging only when modal is open
  if (isModalOpen) {
    debugLogger('🔍 Modal: availableExperiments', availableExperiments);
    debugLogger('🔍 Modal: experimentOptions', experimentOptions);
  }

  const handleClose = () => {
    modestExperimentOverrideActions.setIsModalOpen(false);
    setSelectedExperiment('');
    setSelectedVariant('');
  };

  const handleAddOverride = () => {
    if (!selectedExperiment || !selectedVariant) return;

    // Find the selected variant's display name
    const selectedExperimentGroup = availableExperiments.find(
      (group) => group.groupName === selectedExperiment
    );
    const selectedVariantInfo = selectedExperimentGroup?.variants.find(
      (variant) => variant.name === selectedVariant
    );

    const override: ExperimentOverride = {
      groupName: selectedExperiment,
      variantName: selectedVariant,
      variantId: selectedVariant,
      variantDisplayName: selectedVariantInfo?.displayName || selectedVariant,
    };

    modestExperimentOverrideActions.addOverride(config.appId, override);
    debugLogger('Modest Experiment Override: Added override', override);

    // Reset form
    setSelectedVariant('');
    setSelectedExperiment('');
  };

  const handleRemoveOverride = (groupName: string) => {
    modestExperimentOverrideActions.removeOverride(config.appId, groupName);
    debugLogger('Modest Experiment Override: Removed override for', groupName);
  };

  const handleClearAll = () => {
    modestExperimentOverrideActions.clearAllOverrides(config.appId);
    debugLogger('Modest Experiment Override: Cleared all overrides');
  };

  // Don't render anything if developer overrides are not enabled
  if (!isEnabled) {
    return null;
  }

  return (
    <Modal
      testID={testID}
      open={isModalOpen}
      onClose={handleClose}
      showCloseButton
      size="standard"
    >
      <Box className={styles.modalContainer}>
        <Box className={styles.header}>
          <Typography variant="h4" className={styles.title}>
            Modest Experiment Override
            {config.appName && (
              <span className={styles.appName}> - {config.appName}</span>
            )}
          </Typography>

          <Typography variant="body2" className={styles.subtitle}>
            Override experiment assignments for development and testing
            purposes.
          </Typography>
        </Box>

        <Box className={styles.content}>
          <Box className={styles.mainContent}>
            <AddOverrideSection
              experimentOptions={experimentOptions}
              selectedExperiment={selectedExperiment}
              selectedVariant={selectedVariant}
              selectedExperimentVariants={selectedExperimentVariants}
              onExperimentChange={(experimentId) => {
                setSelectedExperiment(experimentId);
                setSelectedVariant(''); // Reset variant when experiment changes
              }}
              onVariantChange={setSelectedVariant}
              onAddOverride={handleAddOverride}
              debugLogger={debugLogger}
            />

            <CurrentOverridesSection
              currentOverrides={currentOverrides}
              onRemoveOverride={handleRemoveOverride}
              onClearAll={handleClearAll}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box className={styles.footer}>
          <Divider className={styles.divider} />
          <Box className={styles.instructionsBox}>
            <Typography variant="body2" className={styles.subtitle}>
              <strong>Keyboard Shortcut:</strong> Ctrl+Shift+M (platform admins
              only)
            </Typography>
          </Box>
          <Stack>
            <Body size="md">Force onboarding?</Body>
            <SwitchButton
              checked={forceOnboarding}
              onChange={() =>
                onboardingWizardActions.setForceOnboarding(!forceOnboarding)
              }
            />
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

export default DeveloperOverrideModal;
